import { InvitationType } from "./types/invitation";
import type { EnrichmentStatus, OperatorChannel } from "@prisma/client";

export const SESSION_KEY = "sessionId";

/** Cookie and localStorage key for super-admin "View as" role. Only valid when user.role === "SUPER_ADMIN". */
export const VIEW_AS_ROLE_KEY = "eip_view_as_role";

/** Allowed values for view-as role (must match Prisma UserRole). */
export const VIEW_AS_ALLOWED_ROLES = [
  "NORMAL",
  "TRUSTED",
  "SUPER_ADMIN",
] as const;

export type ViewAsRole = (typeof VIEW_AS_ALLOWED_ROLES)[number];

export function isViewAsRole(value: string): value is ViewAsRole {
  return VIEW_AS_ALLOWED_ROLES.includes(value as ViewAsRole);
}

/** Whether this role can access operator tools (e.g. Leads list, delete operators). Use effectiveRole for view-as. */
export function canAccessOperatorTools(role: string): boolean {
  return role === "SUPER_ADMIN" || role === "TRUSTED";
}

/** Super-admin-only view for detailed score data and scoring actions */
export function canViewScoreDetails(role: string): boolean {
  return role === "SUPER_ADMIN";
}

export const BRAND_MATCH_WEEKLY_LIMIT = 15;

// Client-safe OperatorChannel enum values (must match Prisma schema)
// These are defined as string literals to work in both client and server code
export const OperatorChannelValues: Record<OperatorChannel, string> = {
  RESTAURANTS: "RESTAURANTS",
  STREET_FOOD_AND_FOOD_TRUCKS: "STREET_FOOD_AND_FOOD_TRUCKS",
  MEAL_KITS: "MEAL_KITS",
  HEALTHCARE: "HEALTHCARE",
  COLLEGE_UNIVERSITY: "COLLEGE_UNIVERSITY",
  K12_EDUCATION: "K12_EDUCATION",
  RETAIL: "RETAIL",
  GAMING_AND_LODGING: "GAMING_AND_LODGING",
  TRAVEL_AND_RECREATION: "TRAVEL_AND_RECREATION",
  BUSINESS_AND_INDUSTRY: "BUSINESS_AND_INDUSTRY",
  VENDING_MICROMARKET: "VENDING_MICROMARKET",
  DISTRIBUTOR: "DISTRIBUTOR",
  CM_GPO: "CM_GPO",
};

export const OPERATOR_CHANNELS_LABELS: Record<OperatorChannel, string> = {
  RESTAURANTS: "Restaurants",
  COLLEGE_UNIVERSITY: "College & University",
  K12_EDUCATION: "K-12 Education",
  HEALTHCARE: "Healthcare",
  GAMING_AND_LODGING: "Gaming & Lodging",
  TRAVEL_AND_RECREATION: "Travel & Recreation",
  BUSINESS_AND_INDUSTRY: "Business & Industry",
  STREET_FOOD_AND_FOOD_TRUCKS: "Street Food & Food Trucks",
  MEAL_KITS: "Meal Kits",
  RETAIL: "Retail",
  VENDING_MICROMARKET: "Vending & Micromarket",
  DISTRIBUTOR: "Distributor",
  CM_GPO: "CM GPO",
};

export const ENRICHMENT_STATUS_COLORS: Record<EnrichmentStatus, string> = {
  COMPLETED: "#22c55e",
  PENDING: "#f59e0b",
  NOT_STARTED: "#94a3b8",
  ERROR: "#ef4444",
};

export const DISTRIBUTOR_CATEGORIES = {
  BROADLINE_SUPER_REGIONAL:
    "Broadline distributors are comprehensive suppliers, offering a wide range of products to various foodservice operators. They usually have a National or Super Regional footprint. They can also offer special orders and direct shipments at a premium if the product isn't already stocked in a specific warehouse. Orders can be placed through redistributors such as DOT.",
  LOCAL_SPECIALTY:
    "These distributors specialize in specific markets (e.g., seafood, organic produce) and have niche expertise. They operate regionally, not necessarily nationwide, and offer tailored supply chains. Shipments are more difficult to manage because they need to have the volume to pack a truck for delivery.",
  REDISTRIBUTOR:
    "Redistributors purchase products directly from manufacturers and then distribute smaller quantities to foodservice operators who can't handle bulk amounts. They buy from manufacturers in larger quantities and sell to operators in smaller quantities with a mark-up. They can ship LTL (Less-than-truckload), meaning a redistributor can buy pallets from manufacturers and then ship one or two cases to operators.",
  CASH_N_CARRY_DIRECT_TO_OPERATOR:
    "These distributors operate warehouses where operators (such as caterers, nonprofits, and restaurants) can personally select desired food products for purchase. They don't transport goods directly but provide a convenient pickup option. Some are open to the public. Long tail products (products in need of smaller volume, e.g., specialty sauces) are common here. Broadliners may only deliver once or twice a week because they ship with full truckloads, so operators must use cash-n-carry options at times. Manufacturers handle distribution logistics and sell directly to operators.",
  ECOMMERCE_PLATFORM:
    "An Ecommerce platform that allows manufacturers to sell directly to operators or consumers.",
} as const;

export const DISTRIBUTOR_CATEGORIES_LABELS = {
  BROADLINE_SUPER_REGIONAL: "Broadline & Super Regional",
  LOCAL_SPECIALTY: "Local & Specialty",
  REDISTRIBUTOR: "Redistributor",
  CASH_N_CARRY_DIRECT_TO_OPERATOR: "Cash-n-Carry / Direct-to-Operator",
  ECOMMERCE_PLATFORM: "Ecommerce Platform",
} as const;

export const UNSUPPORTED_CHANNELS = [
  OperatorChannelValues.DISTRIBUTOR,
  OperatorChannelValues.CM_GPO,
] as const;

export function getInvitationTypeFromUrl(url: string): InvitationType | null {
  if (url.includes("/super-admin/accept-invitation/")) {
    return InvitationType.SUPER_ADMIN;
  }
  if (url.includes("/accept-invitation/")) {
    return InvitationType.ORGANIZATION;
  }
  return null;
}

export const SOCIAL_MEDIA_DOMAINS = [
  "facebook.com",
  "twitter.com",
  "x.com",
  "instagram.com",
  "linkedin.com",
  "tiktok.com",
  "youtube.com",
  "pinterest.com",
  "snapchat.com",
  "threads.com",
] as const;

export type SocialMediaProvider =
  | "FACEBOOK"
  | "X"
  | "INSTAGRAM"
  | "LINKEDIN"
  | "TIKTOK"
  | "YOUTUBE"
  | "PINTEREST"
  | "SNAPCHAT"
  | "THREADS";

export const SOCIAL_MEDIA_URL_BUILDERS: Record<
  SocialMediaProvider,
  (handle: string) => string
> = {
  FACEBOOK: (handle) => `https://facebook.com/${handle}`,
  X: (handle) => `https://x.com/${handle}`,
  INSTAGRAM: (handle) => `https://instagram.com/${handle}`,
  LINKEDIN: (handle) => `https://linkedin.com/company/${handle}`,
  TIKTOK: (handle) => `https://tiktok.com/@${handle}`,
  YOUTUBE: (handle) =>
    `https://youtube.com/${handle.startsWith("@") ? handle : `@${handle}`}`,
  PINTEREST: (handle) => `https://pinterest.com/${handle}`,
  SNAPCHAT: (handle) => `https://snapchat.com/add/${handle}`,
  THREADS: (handle) => `https://threads.com/@${handle}`,
};

export const COMMERCIAL_OPERATOR_CHANNEL = [
  OperatorChannelValues.RESTAURANTS,
  OperatorChannelValues.STREET_FOOD_AND_FOOD_TRUCKS,
  OperatorChannelValues.MEAL_KITS,
] as const;
export const NON_COMMERCIAL_OPERATOR_CHANNEL = [
  OperatorChannelValues.HEALTHCARE,
  OperatorChannelValues.COLLEGE_UNIVERSITY,
  OperatorChannelValues.K12_EDUCATION,
  OperatorChannelValues.GAMING_AND_LODGING,
  OperatorChannelValues.TRAVEL_AND_RECREATION,
  OperatorChannelValues.BUSINESS_AND_INDUSTRY,
  OperatorChannelValues.VENDING_MICROMARKET,
  OperatorChannelValues.RETAIL,
] as const;

export const OPERATOR_CHANNELS = [
  ...COMMERCIAL_OPERATOR_CHANNEL,
  ...NON_COMMERCIAL_OPERATOR_CHANNEL,
];

export const OPERATOR_CHANNEL_DEFS: Record<OperatorChannel, string> = {
  [OperatorChannelValues.RESTAURANTS]:
    "Food service operators (restaurants, cafes, food trucks, etc.)",
  [OperatorChannelValues.STREET_FOOD_AND_FOOD_TRUCKS]:
    "Street food vendors and food trucks",
  [OperatorChannelValues.MEAL_KITS]: "Meal kit delivery services",
  [OperatorChannelValues.HEALTHCARE]:
    "Hospitals, healthcare facilities, nursing homes with food service",
  [OperatorChannelValues.COLLEGE_UNIVERSITY]:
    "Higher education institutions (colleges, universities) with dining services",
  [OperatorChannelValues.K12_EDUCATION]:
    "K-12 schools (elementary, middle, high schools) with food service",
  [OperatorChannelValues.RETAIL]: "Retail stores with food service components",
  [OperatorChannelValues.GAMING_AND_LODGING]:
    "Casinos, gaming venues, and lodging facilities with food service",
  [OperatorChannelValues.TRAVEL_AND_RECREATION]:
    "Travel and recreation venues (airports, train stations, recreation centers, theme parks) with food service",
  [OperatorChannelValues.BUSINESS_AND_INDUSTRY]:
    "Corporate cafeterias, business dining facilities, and industrial food service operations",
  [OperatorChannelValues.VENDING_MICROMARKET]:
    "Vending machines and micromarkets",
  [OperatorChannelValues.DISTRIBUTOR]:
    "Foodservice distributors that purchase products from manufacturers and supply them to foodservice operators, operating warehouses and managing inventory",
  [OperatorChannelValues.CM_GPO]:
    "Contract Management Organizations (CMOs) that manage dining programs for institutions, and Group Purchasing Organizations (GPOs) that negotiate purchasing contracts for networks of operators",
} as Record<string, string>;

export const FALLBACK_NON_COMMERCIAL_CATEGORY = "Non-Commercial" as const;

export const FALLBACK_NON_COMMERCIAL_CHANNEL = FALLBACK_NON_COMMERCIAL_CATEGORY;

export type DerivedOperatorChannel =
  | OperatorChannel
  | typeof FALLBACK_NON_COMMERCIAL_CHANNEL;
